import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertTriangle } from "lucide-react";

const crc = (n) => `₡ ${Number(n || 0).toLocaleString("es-CR")}`;

export const FrtLoteDialog = ({ open, onOpenChange, data, onAgregar }) => {
  const [sel, setSel] = useState({});
  const [cand, setCand] = useState({});
  const [modoServicio, setModoServicio] = useState({});
  const [conjSel, setConjSel] = useState({});
  const [guardando, setGuardando] = useState(false);

  const resultados = data?.resultados || [];
  const conjuntos = data?.conjuntos || [];

  useEffect(() => {
    const selInit = {}, modoInit = {}, conjInit = {};
    resultados.forEach((r, i) => {
      const sug = (r.servicios_sugeridos || [])[0];
      if (sug?.ya_en_trabajo) selInit[i] = false;
      if (sug && !sug.ya_en_trabajo) modoInit[i] = true;
    });
    conjuntos.forEach((_, gi) => { conjInit[gi] = true; });
    setSel(selInit); setModoServicio(modoInit); setCand({}); setConjSel(conjInit);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [data]);

  const elegida = (r, i) => r.candidatas[cand[i] ?? 0];
  const usaServicio = (r, i) => {
    const sug = (r.servicios_sugeridos || [])[0];
    return !!sug && !sug.ya_en_trabajo && modoServicio[i] !== false;
  };

  // Mismo repuesto que trajo el estimado (por ref_id si existe, si no por nombre+código).
  const idxDeRepuesto = (rep) => resultados.findIndex((r) =>
    rep.ref_id ? r.repuesto.ref_id === rep.ref_id
      : r.repuesto.nombre === rep.nombre && r.repuesto.codigo === rep.codigo);

  // Conjuntos "AMBOS" (izquierdo + derecho) propuestos por el backend, con los índices de las
  // dos filas individuales que reemplazarían y el total/horas de cobrarlas por separado.
  const conjuntosInfo = conjuntos.map((g, gi) => {
    const i1 = idxDeRepuesto(g.repuestos[0]);
    const i2 = idxDeRepuesto(g.repuestos[1]);
    const c1 = i1 >= 0 ? elegida(resultados[i1], i1) : null;
    const c2 = i2 >= 0 ? elegida(resultados[i2], i2) : null;
    return {
      ...g, gi, i1, i2,
      totalIndividual: (c1?.total || 0) + (c2?.total || 0),
      horasIndividual: (c1?.frt_horas || 0) + (c2?.frt_horas || 0),
    };
  }).filter((g) => g.i1 >= 0 && g.i2 >= 0);

  const conjuntosActivos = conjuntosInfo.filter((g) => conjSel[g.gi] !== false);
  const cubiertosIndices = new Set();
  conjuntosActivos.forEach((g) => { cubiertosIndices.add(g.i1); cubiertosIndices.add(g.i2); });

  const marcadosFrt = resultados.map((_, i) => i)
    .filter((i) => {
      const r = resultados[i];
      return !r.sin_frt && r.candidatas.length && !cubiertosIndices.has(i);
    })
    .filter((i) => sel[i] !== false && !usaServicio(resultados[i], i));
  const serviciosSeleccionados = (() => {
    const map = {};
    resultados.forEach((r, i) => {
      if (sel[i] === false || !usaServicio(r, i)) return;
      const sug = r.servicios_sugeridos[0];
      if (!map[sug.servicio_id]) map[sug.servicio_id] = { ...sug, repuesto_ref_id: r.repuesto.ref_id };
    });
    return Object.values(map);
  })();
  const totalFrt = marcadosFrt.reduce((a, i) => a + (elegida(resultados[i], i)?.total || 0), 0);
  const totalServicios = serviciosSeleccionados.reduce((a, s) => a + Number(s.mano_obra || 0), 0);
  const totalConjuntos = conjuntosActivos.reduce((a, g) => a + g.total, 0);
  const total = totalFrt + totalServicios + totalConjuntos;
  const totalLineas = marcadosFrt.length + serviciosSeleccionados.length + conjuntosActivos.length;

  const confirmar = async () => {
    setGuardando(true);
    try {
      const frtLineas = marcadosFrt.map((i) => {
        const r = resultados[i];
        const c = elegida(r, i);
        return { ...c, repuesto: r.repuesto.nombre };
      });
      const conjuntosLineas = conjuntosActivos.map((g) => ({
        id: g.id, operacion: g.operacion, alcance: g.alcance, frt_horas: g.frt_horas, total: g.total,
        repuesto1: g.repuestos[0], repuesto2: g.repuestos[1],
      }));
      if (serviciosSeleccionados.length || frtLineas.length || conjuntosLineas.length) {
        await onAgregar({ servicios: serviciosSeleccionados, frt: frtLineas, conjuntos: conjuntosLineas });
      }
      onOpenChange(false);
      setSel({}); setCand({}); setModoServicio({}); setConjSel({});
    } catch (e) { toast.error(String(e?.message || e)); }
    finally { setGuardando(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="frt-lote-dialog">
        <DialogHeader>
          <DialogTitle>Estimado FRT de los pendientes</DialogTitle>
        </DialogHeader>
        {data && (
          <p className="text-xs text-slate-500 -mt-1" data-testid="frt-lote-tarifa">
            Tarifa: <b>{crc(data.tarifa_hora)}/h</b> (grupo "{data.grupo_tarifa}") · Marcado como <b>ESTIMADO por FRT</b> (no plantilla). Desmarque o cambie la operación si una coincidencia no es correcta.
          </p>
        )}
        <div className="space-y-2">
          {conjuntosInfo.length > 0 && (
            <div className="mb-1 space-y-2 rounded-lg border border-teal-200 bg-teal-50/60 p-2.5" data-testid="frt-lote-conjuntos">
              <p className="text-xs font-semibold text-teal-800">Se cambian los dos lados</p>
              {conjuntosInfo.map((g) => (
                <label key={g.gi} className="flex items-start gap-2 text-xs text-teal-900 cursor-pointer"
                  data-testid={`frt-lote-conjunto-${g.gi}`}>
                  <Checkbox className="mt-0.5" data-testid={`frt-lote-conjunto-check-${g.gi}`}
                    checked={conjSel[g.gi] !== false}
                    onCheckedChange={(v) => setConjSel((s) => ({ ...s, [g.gi]: !!v }))} />
                  <span>
                    <b>{g.operacion}</b> · {g.alcance} — {g.frt_horas} h {crc(g.total)}
                    {g.totalIndividual > g.total && (
                      <span className="text-teal-700"> (en vez de {g.horasIndividual} h {crc(g.totalIndividual)})</span>
                    )}
                    <br />
                    <span className="text-[11px] font-medium text-teal-700">Usar el precio de ambos</span>
                  </span>
                </label>
              ))}
            </div>
          )}
          {resultados.map((r, i) => {
            const sug = (r.servicios_sugeridos || [])[0];
            const mostrarYaIncluido = !!sug && sug.ya_en_trabajo;
            const mostrarRadio = !!sug && !sug.ya_en_trabajo;
            const mostrarFrt = !mostrarRadio || modoServicio[i] === false;
            const hayAlgoQueMarcar = !r.sin_frt || mostrarRadio || mostrarYaIncluido;
            const cubiertoPorConjunto = cubiertosIndices.has(i);
            return (
            <div key={i} data-testid={`frt-lote-fila-${i}`}
              className={`rounded-lg border p-2.5 ${cubiertoPorConjunto ? "border-teal-100 bg-teal-50/30 opacity-60" : r.sin_frt && !sug ? "border-amber-200 bg-amber-50/50" : "border-slate-200"}`}>
              <div className="flex items-start gap-2.5">
                {hayAlgoQueMarcar && (
                  <Checkbox data-testid={`frt-lote-check-${i}`} className="mt-0.5"
                    checked={cubiertoPorConjunto ? false : sel[i] !== false}
                    disabled={cubiertoPorConjunto}
                    onCheckedChange={(v) => setSel((s) => ({ ...s, [i]: !!v }))} />
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-800">
                    {r.repuesto.nombre}
                    {r.repuesto.codigo && <span className="ml-1.5 font-mono text-xs text-slate-400">{r.repuesto.codigo}</span>}
                  </p>
                  {cubiertoPorConjunto && (
                    <p className="mt-0.5 text-xs font-medium text-teal-700" data-testid={`frt-lote-cubierto-conjunto-${i}`}>
                      Incluido en la operación conjunta de ambos lados ↑
                    </p>
                  )}
                  {mostrarYaIncluido && (
                    <p className="mt-0.5 text-xs font-medium text-teal-700" data-testid={`frt-lote-ya-incluido-${i}`}>
                      Mano de obra ya incluida en {sug.servicio_nombre}
                    </p>
                  )}
                  {mostrarRadio && (
                    <div className="mt-1 space-y-1" data-testid={`frt-lote-radio-group-${i}`}>
                      <label className="flex items-center gap-1.5 text-xs text-slate-700 cursor-pointer">
                        <input type="radio" name={`frt-lote-modo-${i}`} checked={modoServicio[i] !== false}
                          onChange={() => setModoServicio((m) => ({ ...m, [i]: true }))}
                          data-testid={`frt-lote-radio-servicio-${i}`} />
                        Usar mano de obra del servicio <b>{sug.servicio_nombre}</b> ({crc(sug.mano_obra)})
                      </label>
                      <label className="flex items-center gap-1.5 text-xs text-slate-700 cursor-pointer">
                        <input type="radio" name={`frt-lote-modo-${i}`} checked={modoServicio[i] === false}
                          onChange={() => setModoServicio((m) => ({ ...m, [i]: false }))}
                          data-testid={`frt-lote-radio-frt-${i}`} />
                        Estimar por FRT
                      </label>
                    </div>
                  )}
                  {mostrarFrt && (r.sin_frt ? (
                    <p className="mt-0.5 inline-flex items-center gap-1 text-xs font-medium text-amber-700" data-testid={`frt-lote-sin-frt-${i}`}>
                      <AlertTriangle size={12} /> {r.nota || "Sin operación FRT encontrada: asígnela a mano."}
                    </p>
                  ) : (
                    <div className="mt-1 space-y-1">
                      {r.candidatas.length > 1 && (
                        <select data-testid={`frt-lote-select-${i}`}
                          value={cand[i] ?? 0}
                          onChange={(e) => setCand((c) => ({ ...c, [i]: Number(e.target.value) }))}
                          className="w-full max-w-md rounded border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700">
                          {r.candidatas.map((c, j) => (
                            <option key={c.id + j} value={j}>
                              {c.operacion}{c.alcance ? ` (${c.alcance})` : ""}{c.seccion ? ` · ${c.seccion}` : ""} — {c.frt_horas}h = {crc(c.total)}
                            </option>
                          ))}
                        </select>
                      )}
                      {(() => { const c = elegida(r, i); return c && (
                        <p className="text-xs text-slate-600" data-testid={`frt-lote-detalle-${i}`}>
                          Operación: <b>{c.operacion}</b>{c.alcance ? ` (${c.alcance})` : ""} · {c.frt_horas}h × {crc(data?.tarifa_hora)}/h = <b className="text-slate-800">{crc(c.total)}</b>
                          <span className="ml-1.5 rounded bg-amber-100 px-1 py-0.5 text-[10px] font-bold uppercase text-amber-700">
                            {(c.metodo === "codigo" || c.metodo === "codigo_base" || c.metodo === "prefijo") ? "match por código de parte" : c.metodo === "original" ? "match por nombre original" : c.metodo === "nombre" ? "match por nombre" : "match por concepto (IA)"}
                          </span>
                        </p>
                      ); })()}
                      {(elegida(r, i)?.razon) && <p className="text-[11px] text-slate-400">{elegida(r, i).razon}</p>}
                      {(elegida(r, i)?.incluye) && <p className="text-[11px] text-teal-700">Ya incluye: {elegida(r, i).incluye} (no cobrar aparte)</p>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            );
          })}
          {resultados.length === 0 && (
            <p className="py-6 text-center text-sm text-slate-400">No hay ítems pendientes sin mano de obra.</p>
          )}
        </div>
        <div className="flex items-center justify-between border-t border-slate-100 pt-3">
          <p className="text-sm text-slate-600" data-testid="frt-lote-total">
            Total estimado: <b className="text-slate-800">{crc(total)}</b> ({totalLineas} línea{totalLineas !== 1 ? "s" : ""})
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="frt-lote-cancelar">Cancelar</Button>
            <Button data-testid="frt-lote-agregar" disabled={guardando || totalLineas === 0}
              onClick={confirmar} className="bg-amber-600 hover:bg-amber-700 text-white">
              {guardando ? "Agregando..." : `Agregar desglose (${totalLineas})`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
